// BMD NTCS Export – Österreich-Standard für Steuerberater
// Format: Semikolon-getrennt, österreichisches Dezimalformat, BOM für Excel
// Direkter Import in BMD NTCS ohne manuelle Nacharbeit

import type { Rechnung } from '../modules/abrechnung/types';

const KONTEN = {
  erloeseAllgemein:   '4100',  // Ärztliche Einkünfte allgemein
  erloeseVgue:        '4000',  // Erlöse VGÜ (steuerfrei)
  erloeseGutachten:   '4200',  // Gutachten
  erloeseBeratung:    '4300',  // Beratung / Begehung / Schulung
  erloeseSonstige:    '4900',  // Sonstige Erlöse
  girokonto:          '1000',  // Bank / Girokonto
  forderungen:        '1200',  // Forderungen aus Leistungen
} as const;

function kontoFuerRechnung(r: Rechnung): string {
  // Konto-Zuordnung nach Leistungstyp (Firma/Mandant als Heuristik)
  const texte = r.positionen.map(p => p.bezeichnung.toLowerCase()).join(' ');
  if (texte.includes('gutachten')) { return KONTEN.erloeseGutachten; }
  if (texte.includes('beratung') || texte.includes('begehung') || texte.includes('schulung')) {
    return KONTEN.erloeseBeratung;
  }
  if (texte.includes('vgü') || texte.includes('vgue') || texte.includes('vorsorge')) {
    return KONTEN.erloeseVgue;
  }
  return KONTEN.erloeseAllgemein;
}

function csvEscape(val: string): string {
  if (val.includes(';') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

function formatBetragAT(cents: number): string {
  return (cents / 100).toLocaleString('de-AT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function convertRechnungenToBmdNtcsCsv(rechnungen: Rechnung[]): string {
  const BOM = '﻿';

  const header = [
    'Belegdatum',
    'Belegnummer',
    'Buchungstext',
    'Konto',
    'Gegenkonto',
    'Umsatzsteuersatz',
    'Bruttobetrag',
    'Nettobetrag',
    'USt-Betrag',
    'Status',
  ].map(csvEscape).join(';');

  const rows = rechnungen
    .filter(r => r.status !== 'entwurf' && r.status !== 'storniert')
    .map(r => {
      const konto     = kontoFuerRechnung(r);
      const gegenkonto = r.status === 'bezahlt' ? KONTEN.girokonto : KONTEN.forderungen;
      const netto     = r.summeNetto;
      const ust       = r.summeUst;
      const brutto    = r.summeBrutto;
      const ustSatz   = netto > 0 ? Math.round((ust / netto) * 100) : 0;
      const buchungstext = `${r.empfaenger.firma} – ${r.rechnungsNr}`;

      return [
        r.datum,
        r.rechnungsNr,
        buchungstext,
        konto,
        gegenkonto,
        `${ustSatz}%`,
        formatBetragAT(brutto),
        formatBetragAT(netto),
        formatBetragAT(ust),
        r.status,
      ].map(csvEscape).join(';');
    });

  return BOM + [header, ...rows].join('\r\n');
}

export function downloadBmdNtcsExport(rechnungen: Rechnung[], zeitraum: string): void {
  const csv      = convertRechnungenToBmdNtcsCsv(rechnungen);
  const datum    = new Date().toISOString().slice(0, 10);
  const filename = `Rechnungen_BMD_NTCS_${zeitraum.replace(/\s/g, '_')}_${datum}.csv`;
  const blob     = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const link     = document.createElement('a');
  link.href      = URL.createObjectURL(blob);
  link.download  = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
